import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@duck/ui'
import { EnvelopeIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { SignupForm } from './SignupForm'
import { GoogleSignInButton } from '../LoginDialog/GoogleSignInButton'
import { LinkedInSignInButton } from '../LoginDialog/LinkedInSignInButton'
import { useTar } from '@/modules'
import { GoogleLoadingView, LinkedInLoadingView } from '../AuthComponents/AuthSocialLoadingViews'
import { StandardLoader } from '../StandardLoader'

type SignupViewProps = {
  /** Reports aggregate busy state up to the shared AuthDialog shell so it can
   *  block dismissal while a social redirect / registration is in flight. */
  onBusyChange?: (busy: boolean) => void
}

export const SignupView = ({ onBusyChange }: SignupViewProps) => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const [isStep2, setIsStep2] = useState(false)
  const [showGoogleExpanded, setShowGoogleExpanded] = useState(false)
  const [showLinkedInExpanded, setShowLinkedInExpanded] = useState(false)
  const [showEmailWarning, setShowEmailWarning] = useState(false)
  const [showEmailForm, setShowEmailForm] = useState(false)
  const {
    isSignupDialogOpen,
    invitationLoading,
    openLoginDialog,
    isRegistering,
    signInWithGoogle,
    signInWithLinkedIn,
    error,
    invitationData
  } = useAuth()
  const { t } = useTar()

  const handleStepChange = useCallback((step: 1 | 2) => {
    setIsStep2(step === 2)
  }, [])

  // Reset state when dialog closes
  useEffect(() => {
    if (!isSignupDialogOpen) {
      setIsStep2(false)
      setShowGoogleExpanded(false)
      setShowLinkedInExpanded(false)
      setShowEmailWarning(false)
      setShowEmailForm(false)
    }
  }, [isSignupDialogOpen])

  const handleExistingUserClick = () => {
    openLoginDialog()
  }

  const handleGoogleSignUp = () => {
    if (isGoogleLoading) return

    // If no invitation, proceed directly
    if (!invitationData?.email) {
      handleGoogleContinue()
      return
    }

    // First click: show expanded view, close LinkedIn if open
    if (!showGoogleExpanded) {
      setShowLinkedInExpanded(false)
      setShowGoogleExpanded(true)
      return
    }
  }

  const handleGoogleContinue = async () => {
    if (isGoogleLoading) return
    try {
      setIsGoogleLoading(true)
      if (signInWithGoogle) {
        await signInWithGoogle()
      }
    } catch (error) {
      console.error('Google sign-up error:', error)
      setIsGoogleLoading(false)
    }
  }

  const handleLinkedInSignUp = () => {
    if (isLinkedInLoading) return

    // If no invitation, proceed directly
    if (!invitationData?.email) {
      handleLinkedInContinue()
      return
    }

    // First click: show expanded view, close Google if open
    if (!showLinkedInExpanded) {
      setShowGoogleExpanded(false)
      setShowLinkedInExpanded(true)
      return
    }
  }

  const handleLinkedInContinue = async () => {
    if (isLinkedInLoading) return
    try {
      setIsLinkedInLoading(true)
      if (signInWithLinkedIn) {
        await signInWithLinkedIn()
      }
    } catch (error) {
      console.error('LinkedIn sign-up error:', error)
      setIsLinkedInLoading(false)
    }
  }

  const handleEmailLinkClick = () => {
    if (showEmailWarning) {
      // Second click: show the email form
      setShowEmailWarning(false)
      setShowEmailForm(true)
    } else {
      // First click: show the warning tooltip
      setShowEmailWarning(true)
    }
  }

  const isAnyLoading = isGoogleLoading || isLinkedInLoading || isRegistering || invitationLoading

  useEffect(() => {
    onBusyChange?.(isAnyLoading)
  }, [isAnyLoading, onBusyChange])

  const invitationLabel = useMemo(() => {
    if (!invitationData) return t('common.signupWithInvitation')
    if (invitationData.role === 'admin') return t('common.adminSignupInvitation')
    if (invitationData.role === 'partner') return t('common.partnerSignupInvitation', { organizationName: invitationData.organizationName })
    if (invitationData.isPublicAssignment) {
      return t('common.signupWithPublicAssignment', { organizationName: invitationData.organizationName, taskName: invitationData.taskName })
    }
    if (invitationData.inviterName || invitationData.organizationName) {
      return t('common.signupWithInvitationFromX', { inviter: invitationData.inviterName || invitationData.organizationName })
    }
    return t('common.signupWithInvitation')
  }, [invitationData, t])

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center gap-2 font-jet-mono text-[10.5px] uppercase tracking-[2px] text-accent">
          <span className="h-px w-3.5 bg-accent/70" />
          {t('auth.signupEyebrow')}
        </div>
        <DialogTitle className="mt-3 text-[26px] font-semibold leading-[1.12] tracking-[-0.6px] text-foreground">
          {t('common.joinAppName')}
        </DialogTitle>
        <p className="mt-2 max-w-[34ch] text-sm leading-relaxed text-muted-foreground">
          {t('auth.signupValueProp')}
        </p>
      </div>

      {invitationLoading ? (
        <div className="flex items-center justify-center p-4 bg-accent/10 rounded-lg">
          <StandardLoader size="sm" />
          <span className="ms-2 text-sm text-muted-foreground">
            {t('common.loading')}
          </span>
        </div>
      ) : isLinkedInLoading ? (
        <LinkedInLoadingView />
      ) : isGoogleLoading ? (
        <GoogleLoadingView />
      ) : (
        <div className="space-y-6">
          {/* Invitation info - always show when present and in step 1 */}
          {!isStep2 && invitationData && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-center">
                <InformationCircleIcon className="w-5 h-5 text-success me-2 flex-shrink-0" />
                <div>
                  <p className="text-md text-primary">{invitationLabel}</p>
                </div>
              </div>
            </div>
          )}

          {/* Registration options */}
          {!isStep2 && !showEmailForm && (
            <div className="flex flex-col space-y-3">
              {/* LinkedIn Sign-Up Button */}
              <div className="flex flex-col">
                <LinkedInSignInButton
                  onClick={handleLinkedInSignUp}
                  isDisabled={isAnyLoading}
                />
                <div
                  data-testid="linkedin-signup-notice"
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    showLinkedInExpanded && invitationData?.email
                      ? 'max-h-96 opacity-100 mt-2'
                      : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="bg-card border border-border rounded-lg p-4 flex flex-col">
                    <p className="text-sm text-foreground mb-3">
                      {t(
                        'auth.registrationViaLinkedInMustBeWithAccountLinkedToEmail'
                      )}{' '}
                      <span className="font-mono font-semibold text-accent">
                        {invitationData?.email}
                      </span>
                    </p>
                    <button
                      onClick={handleLinkedInContinue}
                      disabled={isAnyLoading}
                      className="text-sm text-accent hover:text-foreground transition-colors font-medium self-center"
                    >
                      {t('common.continue')}
                    </button>
                  </div>
                </div>
              </div>
              {/* Google Sign-Up Button */}
              <div className="flex flex-col">
                <GoogleSignInButton
                  onClick={handleGoogleSignUp}
                  isDisabled={isAnyLoading}
                />
                <div
                  data-testid="google-signup-notice"
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    showGoogleExpanded && invitationData?.email
                      ? 'max-h-96 opacity-100 mt-2'
                      : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="bg-card border border-border rounded-lg p-4 flex flex-col">
                    <p className="text-sm text-foreground mb-3">
                      {t(
                        'auth.registrationViaGoogleMustBeWithAccountLinkedToEmail'
                      )}{' '}
                      <span className="font-mono font-semibold text-accent">
                        {invitationData?.email}
                      </span>
                    </p>
                    <button
                      onClick={handleGoogleContinue}
                      disabled={isAnyLoading}
                      className="text-sm text-accent hover:text-foreground transition-colors font-medium self-center"
                    >
                      {t('common.continue')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message - Show in both steps */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-error me-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-error">
                    {t('auth.signupError')}
                  </h4>
                  <p className="text-sm text-error mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Email Sign-Up */}
          {showEmailForm ? (
            <SignupForm onStepChange={handleStepChange} />
          ) : (
            !isStep2 && (
              <div className="space-y-3">
                <div className="flex items-center gap-3.5 before:h-px before:flex-1 before:bg-border/50 before:content-[''] after:h-px after:flex-1 after:bg-border/50 after:content-['']">
                  <span className="font-jet-mono text-[10px] uppercase tracking-[2px] text-muted-foreground">
                    {t('auth.orDivider')}
                  </span>
                </div>
                <TooltipProvider>
                  <Tooltip open={showEmailWarning}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={handleEmailLinkClick}
                        disabled={isAnyLoading}
                        className={`flex h-[42px] w-full items-center justify-center gap-2 rounded-xl border border-dashed text-[13px] font-medium transition-colors ${
                          isAnyLoading
                            ? 'cursor-not-allowed border-border/60 text-muted'
                            : 'border-border text-muted-foreground hover:border-accent/50 hover:text-foreground'
                        }`}
                      >
                        <EnvelopeIcon className="h-[15px] w-[15px]" />
                        {t('auth.continueWithEmail')}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px] text-center p-3">
                      <p className="text-sm">
                        {t('auth.emailSignupWarning')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )
          )}

          {/* Already have an account? */}
          {!isStep2 && !showEmailForm && (
            <p className="border-t border-border/50 pt-4 text-center text-sm text-muted-foreground">
              {t('auth.alreadyHaveAccount')}{' '}
              <button
                type="button"
                onClick={handleExistingUserClick}
                disabled={isAnyLoading}
                className="font-semibold text-accent transition-colors hover:underline disabled:cursor-not-allowed disabled:text-muted"
              >
                {t('auth.logIn')}
              </button>
            </p>
          )}
        </div>
      )}
    </>
  )
}
